import { HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { JwtRefreshService } from '../auth/jwt-refresh.service';
import { AuthRepository } from '../auth/auth.repository';
import { environment } from '@env/environment';

export const JwtErrorInterceptor = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const jwtRefreshService = inject(JwtRefreshService);
  const authRepository = inject(AuthRepository);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Don't try to refresh for the refresh endpoint itself (would cause infinite loop)
      // Also don't refresh for login/logout endpoints
      const isRefreshEndpoint = req.url.includes('/refresh-token');
      const isAuthEndpoint = req.url.includes('/login') || req.url.includes('/logout');
      
      if (error.status === 401 && isApiRequest(req) && !isRefreshEndpoint && !isAuthEndpoint) {
        console.log('[JwtErrorInterceptor] 401 detected, attempting token refresh for:', req.url);
        return from(handle401Error(req, next, jwtRefreshService, authRepository, router)).pipe(
          switchMap(result => result)
        );
      }
      return throwError(() => error);
    })
  );
};

/**
 * Check if the request is to our API
 */
function isApiRequest(req: HttpRequest<any>): boolean {
  return req.url.startsWith(environment.serverUrl);
}

/**
 * Handle 401 Unauthorized errors by attempting token refresh
 */
function handle401Error(req: HttpRequest<any>, next: HttpHandlerFn, jwtRefreshService: JwtRefreshService, authRepository: AuthRepository, router: Router): Promise<Observable<HttpEvent<any>>> {
  console.log('401 error detected, attempting token refresh...');
  
  return jwtRefreshService.refreshToken().then((refreshed: boolean) => {
    if (refreshed) {
      console.log('Token refreshed successfully, retrying request');
      // Retry the original request with new token
      const newReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${authRepository.getAccessToken()}`
        }
      });
      return next(newReq);
    } else {
      console.log('Token refresh failed, redirecting to login');
      // Refresh failed, redirect to login
      router.navigate(['/login']);
      return throwError(() => new Error('Authentication failed'));
    }
  }).catch((error) => {
    console.error('Error during token refresh:', error);
    router.navigate(['/login']);
    return throwError(() => error);
  });
}
